import { type ContextFileUploadSource } from '@/models/analytics/features/context-files';
import { isNetworkCreateError } from '@/utils/http/networkCreateError';
import { logger } from '@/utils/logger/logger';
import {
  finishTracking,
  recordSessionFlowIntent,
  type SessionFlowFailure,
  startTracking,
  type TrackingHandle,
  TrackingKey,
} from '@heidi/runtime/performance';

// `context_upload` client metrics: one intent and one attempt per file the user
// asked to upload, matching the backend's one attempt per document. The attempt
// ends when the server answers; extraction afterwards is the backend's.

export type ContextUploadErrorName =
  | 'ContextUploadFileTooLarge'
  | 'ContextUploadUnsupportedFileType'
  | 'ContextUploadDuplicateFile'
  | 'ContextUploadPreviousUploadFailed'
  | 'ContextUploadForbidden'
  | 'ContextUploadNetworkUnavailable'
  | 'ContextUploadTimeout'
  | 'ContextUploadServerError'
  | 'ContextUploadRejected'
  | 'ContextUploadRateLimited'
  | 'ContextUploadNotPersisted'
  | 'ContextUploadInterrupted'
  | 'ContextUploadCancelled'
  | 'ContextUploadUnclassified';

/**
 * The `context_upload` failure vocabulary, registered with the rest of the
 * session-flow names. `ContextUploadUnclassified` is absent on purpose, so it
 * lands in `_OTHER`.
 */
export const CONTEXT_UPLOAD_FAILURE_TYPES: Readonly<
  Record<Exclude<ContextUploadErrorName, 'ContextUploadUnclassified'>, SessionFlowFailure>
> = {
  // Correct rejections of the file itself: blocked, not a service fault.
  ContextUploadFileTooLarge: { outcome: 'blocked', errorType: 'file_too_large' },
  ContextUploadUnsupportedFileType: {
    outcome: 'blocked',
    errorType: 'unsupported_file_type',
  },
  // The name collides with a healthy file already attached.
  ContextUploadDuplicateFile: { outcome: 'blocked', errorType: 'duplicate_file' },
  // The name matches a file whose last upload failed; the user must clear it
  // before the client will retry it.
  ContextUploadPreviousUploadFailed: {
    outcome: 'blocked',
    errorType: 'previous_upload_failed',
  },
  ContextUploadForbidden: { outcome: 'blocked', errorType: 'permission_denied' },
  ContextUploadNetworkUnavailable: {
    outcome: 'failed',
    errorType: 'network_unavailable',
  },
  ContextUploadTimeout: { outcome: 'failed', errorType: 'request_timeout' },
  ContextUploadServerError: { outcome: 'failed', errorType: 'server_error' },
  ContextUploadRejected: { outcome: 'failed', errorType: 'request_rejected' },
  ContextUploadRateLimited: { outcome: 'failed', errorType: 'rate_limited' },
  // The server answered 200 but stored nothing. It drops a file over a limit
  // and a failed write alike, and names the reason on its own attempt.
  ContextUploadNotPersisted: { outcome: 'failed', errorType: 'not_persisted' },
  // Session expiry or logout ended the upload, not the upload itself.
  ContextUploadInterrupted: { outcome: 'cancelled' },
  // The request was aborted: the user removed the file, or another abort.
  ContextUploadCancelled: { outcome: 'cancelled' },
};

const namedError = (name: ContextUploadErrorName): Error => {
  const error = new Error(name);
  error.name = name;
  return error;
};

/**
 * Maps an upload rejection to a bounded name; an unknown cause lands in
 * `_OTHER`. The raw value is never forwarded: its name may mean something else
 * for another operation in the shared name map.
 */
export const classifyContextUploadError = (error: unknown): ContextUploadErrorName => {
  const { code, response } = (error ?? {}) as {
    code?: unknown;
    response?: { status?: unknown };
  };

  const status = response?.status;
  if (typeof status === 'number') {
    if (status === 401) return 'ContextUploadInterrupted';
    if (status === 403) return 'ContextUploadForbidden';
    if (status === 409) return 'ContextUploadDuplicateFile';
    if (status === 413) return 'ContextUploadFileTooLarge';
    if (status === 429) return 'ContextUploadRateLimited';
    if (status === 408 || status === 504) return 'ContextUploadTimeout';
    if (status >= 500) return 'ContextUploadServerError';
    if (status >= 400) return 'ContextUploadRejected';
  }

  if (code === 'ERR_CANCELED') return 'ContextUploadCancelled';
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return 'ContextUploadTimeout';
  }
  // Desktop offline short-circuits the request as an axios `ERR_NETWORK`.
  if (code === 'ERR_NETWORK' || isNetworkCreateError(error)) {
    return 'ContextUploadNetworkUnavailable';
  }
  return 'ContextUploadUnclassified';
};

// The step and the error's class only: never its message or stack.
const logStepFailure = (step: string, error: unknown): void =>
  logger.warn('[contextUploadTracking] telemetry step failed', {
    step,
    errorName: error instanceof Error ? error.name : typeof error,
  });

// Telemetry must never change the upload's outcome.
const safely = (step: string, run: () => void): void => {
  try {
    run();
  } catch (error) {
    logStepFailure(step, error);
  }
};

/** Emits intent and opens one attempt. Returns no handle if telemetry threw. */
const beginAttempt = (source: ContextFileUploadSource): TrackingHandle | undefined => {
  let handle: TrackingHandle | undefined;
  safely('intent', () => recordSessionFlowIntent(TrackingKey.SCRIBE_CONTEXT_UPLOAD));
  safely('begin', () => {
    handle = startTracking({
      key: TrackingKey.SCRIBE_CONTEXT_UPLOAD,
      instanced: true,
      metadata: { upload_source: source, trigger: 'explicit' },
    });
  });
  return handle;
};

type CancelReason = 'user_removed' | 'unauthorized' | 'interrupted' | 'aborted';

// A bounded name and closed metadata: no raw error, no free-form context.
type FailedClose = {
  name: ContextUploadErrorName;
  metadata?: {
    result?: 'precheck' | 'not_persisted' | 'malformed_response';
    cancel_reason?: CancelReason;
  };
};

// Every cancelled name carries a reason, so the dashboard can tell them apart.
const CANCEL_REASON_BY_NAME: Partial<Record<ContextUploadErrorName, CancelReason>> = {
  ContextUploadInterrupted: 'unauthorized',
  ContextUploadCancelled: 'aborted',
};

// `classify` runs inside the guard, so a throw there cannot reach the upload.
// It falls back to `_OTHER` so the attempt still closes instead of later
// replaying as abandoned.
const failAttempt = (
  step: string,
  handle: TrackingHandle | undefined,
  classify: () => FailedClose
): void =>
  safely(step, () => {
    if (!handle) return;
    let close: FailedClose;
    try {
      close = classify();
    } catch (error) {
      logStepFailure(`${step}_classify`, error);
      close = { name: 'ContextUploadUnclassified' };
    }
    finishTracking({
      handle,
      outcome: 'failed',
      error: namedError(close.name),
      metadata: { result: 'error', ...close.metadata },
    });
  });

export type ContextUploadAttempt = {
  source: ContextFileUploadSource;
  /** True once the user removed this file mid-upload. */
  isRemovedByUser?: () => boolean;
};

/**
 * Runs one file's upload request as a `context_upload` intent and attempt. The
 * request's result or error passes through unchanged. Instanced, so a batch's
 * files each report their own outcome.
 */
export const trackContextUploadAttempt = async <T>(
  attempt: ContextUploadAttempt,
  upload: () => Promise<T>
): Promise<T> => {
  const handle = beginAttempt(attempt.source);

  let result: T;
  try {
    result = await upload();
  } catch (error) {
    failAttempt('fail', handle, () => {
      if (attempt.isRemovedByUser?.()) {
        return {
          name: 'ContextUploadCancelled',
          metadata: { cancel_reason: 'user_removed' },
        };
      }
      const name = classifyContextUploadError(error);
      const cancelReason = CANCEL_REASON_BY_NAME[name];
      return cancelReason
        ? { name, metadata: { cancel_reason: cancelReason } }
        : { name };
    });
    throw error;
  }

  // The legacy HTTP handler resolves a dead-session 401 or a suspended-account
  // 403 with no response, after a logout or redirect. Nothing was uploaded.
  if (result == null) {
    failAttempt('interrupted', handle, () => ({
      name: 'ContextUploadInterrupted',
      metadata: { cancel_reason: 'interrupted' },
    }));
    return result;
  }

  const shape = responseShape(result);
  if (shape === 'empty') {
    failAttempt('complete', handle, () => ({
      name: 'ContextUploadNotPersisted',
      metadata: { result: 'not_persisted' },
    }));
  } else if (shape === 'malformed') {
    // A proxy or captive portal can answer 200 with a body that is no list.
    failAttempt('complete', handle, () => ({
      name: 'ContextUploadUnclassified',
      metadata: { result: 'malformed_response' },
    }));
  } else {
    safely('complete', () => {
      if (!handle) return;
      finishTracking({ handle, metadata: { result: 'success' } });
    });
  }
  return result;
};

// A throwing getter counts as stored: that is a telemetry artefact, not a
// response shape, and telemetry must not invent a failure the user did not see.
const responseShape = (result: unknown): 'stored' | 'empty' | 'malformed' => {
  let data: unknown;
  try {
    data = (result as { data?: unknown }).data;
  } catch {
    return 'stored';
  }
  if (!Array.isArray(data)) return 'malformed';
  return data.length === 0 ? 'empty' : 'stored';
};

export type ContextUploadPrecheckReason =
  | 'unsupported_file_type'
  | 'duplicate_file'
  | 'previous_upload_failed';

const PRECHECK_ERROR: Readonly<
  Record<ContextUploadPrecheckReason, ContextUploadErrorName>
> = {
  unsupported_file_type: 'ContextUploadUnsupportedFileType',
  duplicate_file: 'ContextUploadDuplicateFile',
  previous_upload_failed: 'ContextUploadPreviousUploadFailed',
};

/**
 * The client refused one file before sending it. The user still asked for the
 * upload, so it emits intent and a blocked attempt.
 */
export const recordBlockedContextUpload = (
  source: ContextFileUploadSource,
  reason: ContextUploadPrecheckReason
): void =>
  failAttempt('precheck', beginAttempt(source), () => ({
    name: PRECHECK_ERROR[reason],
    metadata: { result: 'precheck' },
  }));
